import { InputHTMLAttributes, TextareaHTMLAttributes, ReactNode, forwardRef } from 'react';
import './Input.css';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /**
   * Size of the input
   * @default 'base'
   */
  size?: 'sm' | 'base' | 'lg';

  /**
   * Visual state of the input
   * @default undefined
   */
  variant?: 'error' | 'success';

  /**
   * Label text
   */
  label?: string;

  /**
   * Helper text displayed below input
   */
  helperText?: string;

  /**
   * Error message (overrides helperText and sets variant to error)
   */
  error?: string;

  /**
   * Icon to display at the start of input
   */
  iconLeft?: ReactNode;

  /**
   * Icon to display at the end of input
   */
  iconRight?: ReactNode;

  /**
   * Show clear button when input has value
   * @default false
   */
  clearable?: boolean;

  /**
   * Callback when clear button is clicked
   */
  onClear?: () => void;

  /**
   * Show character counter
   * @default false
   */
  showCount?: boolean;

  /**
   * Full width input
   * @default false
   */
  fullWidth?: boolean;

  /**
   * Required field indicator
   * @default false
   */
  required?: boolean;
}

export interface TextareaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> {
  size?: 'sm' | 'base' | 'lg';
  variant?: 'error' | 'success';
  label?: string;
  helperText?: string;
  error?: string;
  showCount?: boolean;
  fullWidth?: boolean;
  required?: boolean;
  /**
   * Auto-resize textarea based on content
   * @default false
   */
  autoResize?: boolean;
}

/**
 * Input component with label, icons, validation states
 *
 * @example
 * ```tsx
 * <Input
 *   label="Email"
 *   type="email"
 *   placeholder="Enter your email"
 *   error={errors.email}
 * />
 *
 * <Input
 *   iconLeft={<SearchIcon />}
 *   placeholder="Search..."
 *   clearable
 * />
 * ```
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(({
  size = 'base',
  variant,
  label,
  helperText,
  error,
  iconLeft,
  iconRight,
  clearable = false,
  onClear,
  showCount = false,
  fullWidth = false,
  required = false,
  className = '',
  value,
  maxLength,
  disabled,
  ...props
}, ref) => {
  const hasError = Boolean(error);
  const finalVariant = hasError ? 'error' : variant;
  const displayText = error || helperText;

  const wrapperClass = [
    'input-wrapper',
    fullWidth && 'input-wrapper-full-width',
    className
  ].filter(Boolean).join(' ');

  const inputClass = [
    'input',
    `input-${size}`,
    finalVariant && `input-${finalVariant}`,
    iconLeft && 'input-with-icon-left',
    iconRight && 'input-with-icon-right',
    (clearable && value) && 'input-with-clear',
    disabled && 'input-disabled'
  ].filter(Boolean).join(' ');

  const handleClear = () => {
    if (onClear) {
      onClear();
    }
  };

  return (
    <div className={wrapperClass}>
      {label && (
        <label className="input-label">
          {label}
          {required && <span className="input-required">*</span>}
        </label>
      )}

      <div className="input-container">
        {iconLeft && (
          <span className="input-icon input-icon-left">
            {iconLeft}
          </span>
        )}

        <input
          ref={ref}
          className={inputClass}
          value={value}
          maxLength={maxLength}
          disabled={disabled}
          aria-invalid={hasError}
          aria-describedby={displayText ? `${props.id}-help` : undefined}
          {...props}
        />

        {clearable && value && !disabled && (
          <button
            type="button"
            className="input-clear"
            onClick={handleClear}
            aria-label="Clear input"
          >
            ✕
          </button>
        )}

        {iconRight && !clearable && (
          <span className="input-icon input-icon-right">
            {iconRight}
          </span>
        )}
      </div>

      {displayText && (
        <p
          id={`${props.id}-help`}
          className={`input-help-text ${hasError ? 'input-help-text-error' : ''}`}
        >
          {displayText}
        </p>
      )}

      {showCount && maxLength && (
        <p className="input-count">
          {String(value || '').length} / {maxLength}
        </p>
      )}
    </div>
  );
});

Input.displayName = 'Input';

/**
 * Textarea component with auto-resize support
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(({
  size = 'base',
  variant,
  label,
  helperText,
  error,
  showCount = false,
  fullWidth = false,
  required = false,
  autoResize = false,
  className = '',
  value,
  maxLength,
  disabled,
  onChange,
  ...props
}, ref) => {
  const hasError = Boolean(error);
  const finalVariant = hasError ? 'error' : variant;
  const displayText = error || helperText;

  const wrapperClass = [
    'input-wrapper',
    fullWidth && 'input-wrapper-full-width',
    className
  ].filter(Boolean).join(' ');

  const textareaClass = [
    'textarea',
    `textarea-${size}`,
    finalVariant && `textarea-${finalVariant}`,
    autoResize && 'textarea-auto-resize',
    disabled && 'textarea-disabled'
  ].filter(Boolean).join(' ');

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (autoResize) {
      e.target.style.height = 'auto';
      e.target.style.height = `${e.target.scrollHeight}px`;
    }
    if (onChange) {
      onChange(e);
    }
  };

  return (
    <div className={wrapperClass}>
      {label && (
        <label className="input-label">
          {label}
          {required && <span className="input-required">*</span>}
        </label>
      )}

      <textarea
        ref={ref}
        className={textareaClass}
        value={value}
        maxLength={maxLength}
        disabled={disabled}
        aria-invalid={hasError}
        aria-describedby={displayText ? `${props.id}-help` : undefined}
        onChange={handleChange}
        {...props}
      />

      {displayText && (
        <p
          id={`${props.id}-help`}
          className={`input-help-text ${hasError ? 'input-help-text-error' : ''}`}
        >
          {displayText}
        </p>
      )}

      {showCount && maxLength && (
        <p className="input-count">
          {String(value || '').length} / {maxLength}
        </p>
      )}
    </div>
  );
});

Textarea.displayName = 'Textarea';

export default Input;
